import { useAuth0 } from '@auth0/auth0-react'

const API_BASE_URL = import.meta.env.VITE_API_URL || import.meta.env.VITE_API_BASE_URL || 'https://api.midwestrailbirds.com/v1'

export interface GolfHole {
  holeNumber: number
  par: number
  yardage: number
  handicap: number
}

export interface GolfCourse {
  courseId: string
  name: string
  location: string
  address?: string
  phone?: string
  website?: string
  holes: GolfHole[]
  totalPar: number
  totalYardage: number
  createdAt: string
  updatedAt: string
}

export interface GolfOuting {
  outingId: string
  name: string
  courseId: string
  courseName: string
  courseLocation: string
  date: string
  format: 'scramble' | 'best-ball' | 'stroke-play'
  teamsCount: number
  description?: string
  sideGames: SideGameConfig[]
  status: 'upcoming' | 'in-progress' | 'completed'
  teams: Team[]
  participants: Participant[]
  createdAt: string
  updatedAt: string
}

export interface Participant {
  userId: string
  email: string
  handicap: number
  joinedAt: string
}

export interface Team {
  teamId: string
  teamName: string
  players: Participant[]
  totalHandicap: number
  averageHandicap: number
}

export interface SideGameConfig {
  type: 'closest-to-pin' | 'longest-drive' | 'skin'
  hole: number
}

export interface Score {
  outingId: string
  teamId: string
  holeNumber: number
  score: number
  players?: { userId: string; individualScore: number }[]
  updatedAt: string
}

export interface SideGameResult {
  outingId: string
  gameType: string
  hole: number
  winners: { userId: string; value: number }[]
  updatedAt: string
}

export interface LeaderboardEntry {
  teamId: string
  teamName: string
  players: Participant[]
  holes: { [holeNumber: number]: number }
  totalScore: number
  holesCompleted: number
  toPar: number
}

export interface Leaderboard {
  leaderboard: LeaderboardEntry[]
  course: GolfCourse
  outing: GolfOuting
}

export interface PlayerStats {
  userId: string
  totalOutings: number
  totalRounds: number
  averageScore: number
  bestScore: number | null
  worstScore: number | null
  currentHandicap: number
  outings: {
    outingId: string
    name: string
    date: string
    courseName: string
  }[]
}

class GolfApi {
  private getAccessTokenSilently: (() => Promise<string>) | null = null

  setAuth(getAccessTokenSilently: () => Promise<string>) {
    this.getAccessTokenSilently = getAccessTokenSilently
  }

  private async getHeaders(): Promise<HeadersInit> {
    if (!this.getAccessTokenSilently) {
      throw new Error('Auth not initialized. Call setAuth() first.')
    }
    
    try {
      const token = await this.getAccessTokenSilently()
      return {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      }
    } catch (error) {
      throw new Error('Authentication failed')
    }
  }

  // ============================================
  // COURSE API
  // ============================================

  async getCourses(): Promise<GolfCourse[]> {
    const headers = await this.getHeaders()
    const response = await fetch(`${API_BASE_URL}/golf/courses`, {
      method: 'GET',
      headers,
      credentials: 'include'
    })

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }

    const data = await response.json()
    return data.courses
  }

  async getCourse(courseId: string): Promise<GolfCourse> {
    const headers = await this.getHeaders()
    const response = await fetch(`${API_BASE_URL}/golf/courses/${courseId}`, {
      method: 'GET',
      headers,
      credentials: 'include'
    })

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }

    const data = await response.json()
    return data.course
  }

  async createCourse(course: Partial<GolfCourse>): Promise<GolfCourse> {
    const headers = await this.getHeaders()
    const response = await fetch(`${API_BASE_URL}/golf/courses`, {
      method: 'POST',
      headers,
      credentials: 'include',
      body: JSON.stringify(course)
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      throw new Error(errorData.error || `HTTP error! status: ${response.status}`)
    }

    const data = await response.json()
    return data.course
  }

  async updateCourse(courseId: string, updates: Partial<GolfCourse>): Promise<GolfCourse> {
    const headers = await this.getHeaders()
    const response = await fetch(`${API_BASE_URL}/golf/courses/${courseId}`, {
      method: 'PATCH',
      headers,
      credentials: 'include',
      body: JSON.stringify(updates)
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      throw new Error(errorData.error || `HTTP error! status: ${response.status}`)
    }

    const data = await response.json()
    return data.course
  }

  async deleteCourse(courseId: string): Promise<void> {
    const headers = await this.getHeaders()
    const response = await fetch(`${API_BASE_URL}/golf/courses/${courseId}`, {
      method: 'DELETE',
      headers,
      credentials: 'include'
    })

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }
  }

  // ============================================
  // OUTING API
  // ============================================

  async getOutings(filters?: { status?: string; year?: number }): Promise<GolfOuting[]> {
    const headers = await this.getHeaders()
    const params = new URLSearchParams()
    if (filters?.status) params.append('status', filters.status)
    if (filters?.year) params.append('year', filters.year.toString())

    const url = `${API_BASE_URL}/golf/outings${params.toString() ? `?${params.toString()}` : ''}`
    const response = await fetch(url, {
      method: 'GET',
      headers,
      credentials: 'include'
    })

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }

    const data = await response.json()
    return data.outings
  }

  async getOuting(outingId: string): Promise<GolfOuting> {
    const headers = await this.getHeaders()
    const response = await fetch(`${API_BASE_URL}/golf/outings/${outingId}`, {
      method: 'GET',
      headers,
      credentials: 'include'
    })

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }

    const data = await response.json()
    return data.outing
  }

  async createOuting(outing: Partial<GolfOuting>): Promise<GolfOuting> {
    const headers = await this.getHeaders()
    const response = await fetch(`${API_BASE_URL}/golf/outings`, {
      method: 'POST',
      headers,
      credentials: 'include',
      body: JSON.stringify(outing)
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      throw new Error(errorData.error || `HTTP error! status: ${response.status}`)
    }

    const data = await response.json()
    return data.outing
  }

  async updateOuting(outingId: string, updates: Partial<GolfOuting>): Promise<GolfOuting> {
    const headers = await this.getHeaders()
    const response = await fetch(`${API_BASE_URL}/golf/outings/${outingId}`, {
      method: 'PATCH',
      headers,
      credentials: 'include',
      body: JSON.stringify(updates)
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      throw new Error(errorData.error || `HTTP error! status: ${response.status}`)
    }

    const data = await response.json()
    return data.outing
  }

  async deleteOuting(outingId: string): Promise<void> {
    const headers = await this.getHeaders()
    const response = await fetch(`${API_BASE_URL}/golf/outings/${outingId}`, {
      method: 'DELETE',
      headers,
      credentials: 'include'
    })

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }
  }

  async joinOuting(
    outingId: string,
    handicap?: number
  ): Promise<{ message?: string; participants: Participant[]; teams?: Team[] }> {
    const headers = await this.getHeaders()
    const response = await fetch(`${API_BASE_URL}/golf/outings/${outingId}/join`, {
      method: 'POST',
      headers,
      credentials: 'include',
      body: JSON.stringify({ handicap })
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      throw new Error(errorData.error || `HTTP error! status: ${response.status}`)
    }

    return await response.json()
  }

  // ============================================
  // TEAM API
  // ============================================

  async generateTeams(outingId: string): Promise<Team[]> {
    const headers = await this.getHeaders()
    const response = await fetch(`${API_BASE_URL}/golf/outings/${outingId}/teams/generate`, {
      method: 'POST',
      headers,
      credentials: 'include'
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      throw new Error(errorData.error || `HTTP error! status: ${response.status}`)
    }

    const data = await response.json()
    return data.teams
  }

  async updateTeams(outingId: string, teams: Team[]): Promise<Team[]> {
    const headers = await this.getHeaders()
    const response = await fetch(`${API_BASE_URL}/golf/outings/${outingId}/teams`, {
      method: 'PUT',
      headers,
      credentials: 'include',
      body: JSON.stringify({ teams })
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      throw new Error(errorData.error || `HTTP error! status: ${response.status}`)
    }

    const data = await response.json()
    return data.teams
  }

  // ============================================
  // SCORING API
  // ============================================

  async getScores(outingId: string): Promise<Score[]> {
    const headers = await this.getHeaders()
    const response = await fetch(`${API_BASE_URL}/golf/outings/${outingId}/scores`, {
      method: 'GET',
      headers,
      credentials: 'include'
    })

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }

    const data = await response.json()
    return data.scores
  }

  async updateScore(
    outingId: string,
    teamId: string,
    holeNumber: number,
    score: number,
    players?: { userId: string; individualScore: number }[]
  ): Promise<Score> {
    const headers = await this.getHeaders()
    const response = await fetch(
      `${API_BASE_URL}/golf/outings/${outingId}/scores/${teamId}/holes/${holeNumber}`,
      {
        method: 'PUT',
        headers,
        credentials: 'include',
        body: JSON.stringify({ score, players })
      }
    )

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      throw new Error(errorData.error || `HTTP error! status: ${response.status}`)
    }

    const data = await response.json()
    return data.score
  }

  async getLeaderboard(outingId: string): Promise<Leaderboard> {
    const headers = await this.getHeaders()
    const response = await fetch(`${API_BASE_URL}/golf/outings/${outingId}/leaderboard`, {
      method: 'GET',
      headers,
      credentials: 'include'
    })

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }

    return await response.json()
  }

  // ============================================
  // SIDE GAMES API
  // ============================================

  async getSideGames(outingId: string): Promise<SideGameResult[]> {
    const headers = await this.getHeaders()
    const response = await fetch(`${API_BASE_URL}/golf/outings/${outingId}/side-games`, {
      method: 'GET',
      headers,
      credentials: 'include'
    })

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }

    const data = await response.json()
    return data.sideGames
  }

  async updateSideGame(
    outingId: string,
    gameType: string,
    hole: number,
    winners: { userId: string; value: number }[]
  ): Promise<SideGameResult> {
    const headers = await this.getHeaders()
    const response = await fetch(
      `${API_BASE_URL}/golf/outings/${outingId}/side-games/${gameType}`,
      {
        method: 'PUT',
        headers,
        credentials: 'include',
        body: JSON.stringify({ hole, winners })
      }
    )

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      throw new Error(errorData.error || `HTTP error! status: ${response.status}`)
    }

    const data = await response.json()
    return data.sideGame
  }

  // ============================================
  // PLAYER STATS API
  // ============================================

  async getPlayerStats(userId: string): Promise<PlayerStats> {
    const headers = await this.getHeaders()
    const response = await fetch(`${API_BASE_URL}/golf/players/${userId}/stats`, {
      method: 'GET',
      headers,
      credentials: 'include'
    })

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }

    const data = await response.json()
    return data.stats
  }
}

// Export singleton instance
export const golfApi = new GolfApi()

// Hook to use the golf API with Auth0
export function useGolfApi() {
  const { getAccessTokenSilently } = useAuth0()
  
  // Initialize auth on first use
  if (!golfApi['getAccessTokenSilently']) {
    golfApi.setAuth(getAccessTokenSilently)
  }
  
  return golfApi
}

